import React, { useState } from 'react';
import { Globe, Edit2, Check, X } from 'lucide-react';

interface Territory {
  ask: number;
  take: number;
}

interface TerritoryBreakdownProps {
  title: string;
  director: string;
  actor: any;
  territories: {
    [key: string]: {
      [key: string]: Territory;
    };
  };
  baseValues: {
    [key: string]: {
      [key: string]: Territory;
    };
  };
  onValueChange: (region: string, territory: string, field: 'ask' | 'take', value: number) => void;
  readOnly?: boolean;
}

const TerritoryBreakdown: React.FC<TerritoryBreakdownProps> = ({ 
  title, 
  director, 
  territories,
  baseValues,
  onValueChange,
  readOnly = false
}) => {
  const [editingCell, setEditingCell] = useState<{
    region: string;
    territory: string;
    field: 'ask' | 'take';
  } | null>(null);
  const [editValue, setEditValue] = useState('');

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(value);
  };

  const calculateRegionTotal = (region: { [key: string]: Territory }) => {
    return Object.values(region).reduce(
      (acc, territory) => ({
        ask: acc.ask + territory.ask,
        take: acc.take + territory.take
      }),
      { ask: 0, take: 0 }
    );
  };

  const calculateGrandTotal = () => {
    return Object.values(territories).reduce(
      (acc, region) => {
        const regionTotal = calculateRegionTotal(region);
        return {
          ask: acc.ask + regionTotal.ask,
          take: acc.take + regionTotal.take
        };
      },
      { ask: 0, take: 0 }
    );
  };

  const handleEdit = (region: string, territory: string, field: 'ask' | 'take', currentValue: number) => {
    if (!readOnly) {
      setEditingCell({ region, territory, field });
      setEditValue(currentValue.toString());
    }
  };

  const handleSave = () => {
    if (editingCell && !readOnly) {
      const value = Number(editValue);
      if (!isNaN(value) && value >= 0) {
        onValueChange(editingCell.region, editingCell.territory, editingCell.field, value);
      }
      setEditingCell(null);
    }
  };

  const handleCancel = () => {
    setEditingCell(null);
  };

  const renderEditableCell = (region: string, territory: string, field: 'ask' | 'take', value: number) => {
    const isEditing = editingCell?.region === region && 
                     editingCell?.territory === territory && 
                     editingCell?.field === field;

    if (isEditing) {
      return (
        <div className="flex items-center justify-end space-x-2">
          <input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-24 px-2 py-1 text-right border rounded"
            autoFocus
          />
          <button onClick={handleSave} className="text-green-600 hover:text-green-700">
            <Check className="h-4 w-4" />
          </button>
          <button onClick={handleCancel} className="text-red-600 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-end space-x-2">
        <span>{formatCurrency(value)}</span>
        {!readOnly && (
          <button 
            onClick={() => handleEdit(region, territory, field, value)}
            className="text-gray-400 hover:text-gray-600"
          >
            <Edit2 className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-6 bg-yellow-50 border-b border-yellow-100">
        <h2 className="text-xl font-bold text-center mb-2">
          CINELAUNCH INTERNATIONAL SALES ESTIMATES
        </h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-bold">Title:</span> {title}
          </div>
          <div>
            <span className="font-bold">Director:</span> {director}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Territories
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Base Ask
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Base Take
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Adjusted Ask
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Adjusted Take
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Object.entries(territories).map(([regionName, region]) => (
              <React.Fragment key={regionName}>
                {Object.entries(region).map(([territory, values]) => (
                  <tr key={`${regionName}-${territory}`} className="hover:bg-gray-50">
                    <td className="px-6 py-2 text-sm text-gray-900">
                      {territory.replace(/([A-Z])/g, ' $1').trim()}
                    </td>
                    <td className="px-6 py-2 text-sm text-gray-900 text-right">
                      {renderEditableCell(regionName, territory, 'ask', baseValues[regionName][territory].ask)}
                    </td>
                    <td className="px-6 py-2 text-sm text-gray-900 text-right">
                      {renderEditableCell(regionName, territory, 'take', baseValues[regionName][territory].take)}
                    </td>
                    <td className="px-6 py-2 text-sm text-gray-900 text-right">
                      {formatCurrency(values.ask)}
                    </td>
                    <td className="px-6 py-2 text-sm text-gray-900 text-right">
                      {formatCurrency(values.take)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-medium">
                  <td className="px-6 py-2 text-sm text-gray-900">
                    Total {regionName.replace(/([A-Z])/g, ' $1').trim()}
                  </td>
                  <td className="px-6 py-2 text-sm text-gray-900 text-right">
                    {formatCurrency(calculateRegionTotal(baseValues[regionName]).ask)}
                  </td>
                  <td className="px-6 py-2 text-sm text-gray-900 text-right">
                    {formatCurrency(calculateRegionTotal(baseValues[regionName]).take)}
                  </td>
                  <td className="px-6 py-2 text-sm text-gray-900 text-right">
                    {formatCurrency(calculateRegionTotal(region).ask)}
                  </td>
                  <td className="px-6 py-2 text-sm text-gray-900 text-right">
                    {formatCurrency(calculateRegionTotal(region).take)}
                  </td>
                </tr>
              </React.Fragment>
            ))}
            <tr className="bg-yellow-50 font-bold">
              <td className="px-6 py-3 text-sm text-gray-900">
                Total MG Estimate
              </td>
              <td className="px-6 py-3 text-sm text-gray-900 text-right">
                {formatCurrency(calculateGrandTotal().ask)}
              </td>
              <td className="px-6 py-3 text-sm text-gray-900 text-right">
                {formatCurrency(calculateGrandTotal().take)}
              </td>
              <td className="px-6 py-3 text-sm text-gray-900 text-right">
                {formatCurrency(calculateGrandTotal().ask)}
              </td>
              <td className="px-6 py-3 text-sm text-gray-900 text-right">
                {formatCurrency(calculateGrandTotal().take)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="p-4 bg-gray-50 text-xs text-gray-500 italic">
        The foregoing projections are estimates only and include estimates expected. No representation is being made that
        these estimates will be realized. Actual results may vary materially from the projections for numerous reasons
        including, but not limited to, prevailing market conditions, territorial and global economic climate, the final cast and
        creative elements of the Picture and U.S. theatrical release. Consequently, no guarantee is presented or implied as to
        the accuracy of specific projections or predictive statements contained herein. The foregoing projections are strictly
        confidential and, without the prior written consent of Cinelaunch, may not be disclosed by the party to whom they
        are furnished to any other person.
      </div>
    </div>
  );
};

export default TerritoryBreakdown;